import React, { useEffect, useMemo, useState } from "react";
import { Container, Col, Row, Button, Form, Fade, Carousel } from "react-bootstrap";
import { useSearchParams, Link } from "react-router-dom";
import Masonry from "react-masonry-css";
import FullPageSpinner from "../../components/spinner/FullPageSpinner";
import { apiCall, PageScroller } from "../../helpers/api";
import { useImageContext } from "../../hooks/useImageContext";
import { getImageUrl } from "../../helpers/images";
import AlertModal from "../../components/alerts/alert";
import ReviewPanel from "../../components/review/ReviewPanel";
import "./Campaigns.css";
import Topbar from "../../components/topbar/Topbar";
import Footer from "../../components/footer/Footer";
import NoAccess from "../../images/no-access.png";
import { useAuthContext } from "../../hooks/useAuthContext";

const CONTRIBUTOR_PREVIEW_COUNT = 4;

function getCurrentVersionIndex(content) {
  const historyVersions = Array.isArray(content?.versions) ? content.versions : [];
  const hasCurrentVersion = content?.assetUrl ? 1 : 0;
  return historyVersions.length + hasCurrentVersion - 1;
}

function getContentBidId(content) {
  return content?.bidID || content?.bidId || null;
}

function getDashboardReviewKey(content, versionIndex = null) {
  const resolvedVersionIndex = typeof versionIndex == "number" ? versionIndex : getCurrentVersionIndex(content);
  const bidId = getContentBidId(content) || "unknown";
  const contributionId = content?.contentID || content?.id || "unknown";
  return `${bidId}_${contributionId}_${resolvedVersionIndex}`;
}

function isContentReviewable(content) {
  const versionIndex = getCurrentVersionIndex(content);
  if (versionIndex < 0) {
    return false;
  }
  const meta =
    typeof content?.assetUrl == "object" && content?.assetUrl?.meta && typeof content.assetUrl.meta == "object"
      ? content.assetUrl.meta
      : {};
  return meta.reviewed !== true;
}

function getReviewState(content, localReviews = {}) {
  const localKey = getDashboardReviewKey(content);
  const localReview = localReviews[localKey];
  if (localReview && typeof localReview.approved == "boolean") {
    return {
      reviewed: true,
      approved: localReview.approved,
      reviewNotes: localReview.reviewNotes || "",
      isDraft: true,
    };
  }

  const meta =
    typeof content?.assetUrl == "object" && content?.assetUrl?.meta && typeof content.assetUrl.meta == "object"
      ? content.assetUrl.meta
      : {};

  if (meta.reviewed === true && typeof meta.approved == "boolean") {
    return {
      reviewed: true,
      approved: meta.approved,
      reviewNotes: meta.reviewNotes || "",
      isDraft: false,
    };
  }

  return {
    reviewed: false,
    approved: null,
    reviewNotes: "",
    isDraft: false,
  };
}

function getBidStatePillClass(state) {
  if (["Completed"].includes(state)) {
    return "bg-success";
  }
  if (["Marked as Complete", "Ready for Completion"].includes(state)) {
    return "bg-info text-dark";
  }
  if (["In Review", "Changes Requested", "In Progress"].includes(state)) {
    return "bg-warning text-dark";
  }
  if (["Accepted", "Pending"].includes(state)) {
    return "bg-primary";
  }
  if (["Rejected", "Cancelled"].includes(state)) {
    return "bg-danger";
  }
  return "bg-secondary";
}

function getGroupBidId(group) {
  if (!group) {
    return null;
  }
  if (group.bidID || group.bidId) {
    return group.bidID || group.bidId;
  }

  const fallbackContent = Array.isArray(group.contents)
    ? group.contents.find((content) => content?.bidID || content?.bidId)
    : null;
  return fallbackContent ? fallbackContent.bidID || fallbackContent.bidId : null;
}

export default function CampaignUGCDashboard() {
  let [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(true);
  const [requestingDownload, setRequestingDownload] = useState(false);
  const [campaignData, setCampaignData] = useState([]);
  const [campaignDataIDs, setCampaignDataIDs] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [contributors, setContributors] = useState([]);
  const [filteredContributors, setFilteredContributors] = useState([]);
  const [selectedInfluencer, setSelectedInfluencer] = useState(searchParams.get("influencerID"));
  const [searchFilters, setSearchFilters] = useState({ search: "", posts: true, notReviewed: false, showAll: false });
  const { providers } = useImageContext();
  const { currentProfile } = useAuthContext();
  const [showFilters, setShowFilters] = useState(false);
  const [openCarousel, setOpenCarousel] = useState(null);
  const [showAlert, setAlert] = useState({ display: "toast", visible: false, title: "", content: "" });
  const [dashboardReviews, setDashboardReviews] = useState({});
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const [reviewPanelGroup, setReviewPanelGroup] = useState(null);
  const [reviewPanelSubmissions, setReviewPanelSubmissions] = useState([]);
  const [reviewPanelLocalReviews, setReviewPanelLocalReviews] = useState({});
  const [reviewPanelReviewNotes, setReviewPanelReviewNotes] = useState({});
  const [reviewPanelSelectedVersion, setReviewPanelSelectedVersion] = useState({});
  const [reviewPanelExpanded, setReviewPanelExpanded] = useState({});
  const [scrollerRefreshToken, setScrollerRefreshToken] = useState(0);

  const breakpointColumnsObj = {
    default: 4,
    1100: 5,
    768: 3,
    576: 2,
  };

  const campaignID = searchParams.get("id");

  const reviewSummaryByInfluencer = useMemo(() => {
    const summary = {};

    campaignData
      .filter((content) => String(content.platform || "self").toLowerCase() == "self")
      .forEach((content) => {
        const influencerId = content.influencerID;
        if (!summary[influencerId]) {
          summary[influencerId] = { approved: 0, rejected: 0, pending: 0, total: 0 };
        }

        summary[influencerId].total += 1;
        const state = getReviewState(content, dashboardReviews);
        if (!state.reviewed) {
          summary[influencerId].pending += 1;
        } else if (state.approved) {
          summary[influencerId].approved += 1;
        } else {
          summary[influencerId].rejected += 1;
        }
      });

    return summary;
  }, [campaignData, dashboardReviews]);

  const groupedCampaignData = useMemo(() => {
    let groups = {};
    campaignData
      .filter((content) => {
        return (
          (selectedInfluencer == null || selectedInfluencer == content.influencerID) &&
          String(content.platform || "self").toLowerCase() == "self"
        );
      })
      .forEach((content) => {
        if (!groups[content.influencerID]) {
          groups[content.influencerID] = {
            id: content.influencerID,
            bidID: content.bidID || content.bidId || null,
            state: content.state || "Unknown",
            name: content.name,
            profileImage: content.profileImage,
            contents: [],
          };
        }
        if (!groups[content.influencerID].bidID && (content.bidID || content.bidId)) {
          groups[content.influencerID].bidID = content.bidID || content.bidId;
        }
        groups[content.influencerID].contents.push(content);
      });

    return Object.values(groups).sort((a, b) => {
      if (b.contents.length != a.contents.length) {
        return b.contents.length - a.contents.length;
      }
      return a.name.localeCompare(b.name);
    });
  }, [campaignData, selectedInfluencer]);

  const hideShowFilters = () => {
    setShowFilters((current) => !current);
  };

  async function getCampaign(currentCampaignID) {
    setIsLoaded(false);
    var response = await apiCall(`campaign/ugcDashboard?id=${currentCampaignID}&platform=self`, { method: "GET" });
    if (response["success"]) {
      setCampaign(response["success"]["campaign"]);
      setContributors(response["success"]["contributors"]);
      setIsLoaded(true);
      return;
    }

    setError(response["failure"]?.["error"] || "Could not load campaign dashboard.");
    setIsLoaded(true);
  }

  async function downloadContent() {
    setRequestingDownload(true);
    var response = await apiCall(`campaign/dashboard/download?id=${campaignID}`, { method: "GET" });
    if (response["success"]) {
      setAlert({
        display: "toast",
        visible: true,
        title: "Download Requested",
        content: "You will receive an email with your content shortly.",
        className: "bg-success text-white",
      });
    } else {
      setAlert({
        display: "toast",
        visible: true,
        title: "Failed to request download",
        content: response["failure"]["error"],
        className: "bg-danger text-white",
      });
    }
    setRequestingDownload(false);
  }

  async function getMoreContent(event, result) {
    result = await result;
    if (!result || !result["campaignContent"]) {
      return;
    }

    var tempCampaignData = structuredClone(campaignData);
    var tempCampaignDataIDs = structuredClone(campaignDataIDs);
    for (var x in result["campaignContent"]) {
      if (!tempCampaignDataIDs.includes(result["campaignContent"][x]["id"])) {
        tempCampaignData.push(result["campaignContent"][x]);
        tempCampaignDataIDs.push(result["campaignContent"][x]["id"]);
      }
    }
    setCampaignData(tempCampaignData);
    setCampaignDataIDs(tempCampaignDataIDs);
  }

  function getStoredAssetUrl(asset, isVideo = false) {
    if (!asset) {
      return null;
    }
    if (typeof asset == "string") {
      return asset;
    }
    if (asset.downloadUrl) {
      return asset.downloadUrl;
    }
    if (asset.url) {
      return asset.url;
    }
    if (asset.id) {
      return getImageUrl(providers, asset, null, false, isVideo);
    }
    return null;
  }

  function getVideoSource(content) {
    return (
      getStoredAssetUrl(content.assetMedia, true) ||
      getStoredAssetUrl(content.videoDownloadUrl, true) ||
      getStoredAssetUrl(content.assetUrl, true)
    );
  }

  function getImageSource(content) {
    return getStoredAssetUrl(content.assetUrl, false);
  }

  function getCarouselImageUrl(image) {
    if (!image) {
      return null;
    }
    if (typeof image == "string") {
      return image;
    }
    if (image.downloadUrl) {
      return image.downloadUrl;
    }
    if (image.url) {
      return image.url;
    }
    return getStoredAssetUrl(image, false);
  }

  function getImageSlides(content) {
    if (content.assetType != "image") {
      return [];
    }

    const versions = Array.isArray(content.versions) ? content.versions : [];
    const allVersioned = [...versions, content.assetUrl].filter(Boolean);
    const slides = allVersioned
      .map((item, index) => {
        const url = getCarouselImageUrl(item);
        if (!url) {
          return null;
        }
        return {
          url,
          caption: typeof item == "object" ? item.name || `Version ${index + 1}` : `Version ${index + 1}`,
        };
      })
      .filter(Boolean);

    if (slides.length > 0) {
      return slides;
    }

    const fallbackUrl = getImageSource(content);
    if (!fallbackUrl) {
      return [];
    }
    return [{ url: fallbackUrl, caption: content.text || "Content" }];
  }

  function changeContentImage(index) {
    var tempContent = structuredClone(openCarousel);
    tempContent.openImage = index;
    setOpenCarousel(tempContent);
  }

  function toggleSelectedInfluencer(influencerID) {
    let currentUrlParams = new URLSearchParams(window.location.search);
    if (selectedInfluencer == influencerID) {
      setSelectedInfluencer(null);
      currentUrlParams.delete("influencerID");
    } else {
      setSelectedInfluencer(influencerID);
      currentUrlParams.set("influencerID", influencerID);
    }
    setCampaignDataIDs([]);
    setCampaignData([]);
    let newUrl = window.location.origin + window.location.pathname + "?" + currentUrlParams.toString();
    window.history.replaceState({ path: newUrl }, "", newUrl);
  }

  function updateSearchFilters(key, value) {
    setSearchFilters((currentFilter) => {
      if (key == "update") {
        return { ...currentFilter };
      }
      return { ...currentFilter, [key]: value };
    });
  }

  function setDashboardContentReview(content, approved) {
    if (!isContentReviewable(content)) {
      return;
    }

    const key = getDashboardReviewKey(content);
    setDashboardReviews((currentReviews) => ({
      ...currentReviews,
      [key]: {
        approved,
        reviewNotes: currentReviews[key]?.reviewNotes || "",
      },
    }));
  }

  function setDashboardContentReviewNote(content, reviewNotes) {
    if (!isContentReviewable(content)) {
      return;
    }

    const key = getDashboardReviewKey(content);
    const currentState = getReviewState(content, dashboardReviews);
    setDashboardReviews((currentReviews) => ({
      ...currentReviews,
      [key]: {
        approved:
          typeof currentReviews[key]?.approved == "boolean"
            ? currentReviews[key].approved
            : typeof currentState.approved == "boolean"
              ? currentState.approved
              : null,
        reviewNotes,
      },
    }));
  }

  function resetReviewPanelState() {
    setReviewPanelGroup(null);
    setReviewPanelSubmissions([]);
    setReviewPanelLocalReviews({});
    setReviewPanelReviewNotes({});
    setReviewPanelSelectedVersion({});
    setReviewPanelExpanded({});
  }

  function toReviewPanelSubmission(content) {
    const versionsRaw = Array.isArray(content?.versions) ? content.versions : [];
    const normalizedVersions = versionsRaw.map((version) => (typeof version == "string" ? { url: version } : version));

    let latest =
      normalizedVersions.length > 0
        ? normalizedVersions[normalizedVersions.length - 1]
        : typeof content.assetUrl == "object"
          ? { ...content.assetUrl }
          : content.assetUrl;
    const normalizedLatest = typeof latest == "string" ? { url: latest } : latest;

    return {
      ...content,
      id: content.contentID || content.id,
      title: content.assetType || "Submission",
      description: content.description || content.text || "",
      assetType: content.assetType,
      assetUrl: normalizedLatest,
      versions: normalizedVersions,
    };
  }

  function openGroupReviewPanel(group) {
    const bidId = getGroupBidId(group);
    if (!bidId) {
      setAlert({
        display: "toast",
        visible: true,
        title: "Cannot open review",
        content: "Missing bid reference for this influencer.",
        className: "bg-danger text-white",
      });
      return;
    }

    const submissions = group.contents.map((content) => toReviewPanelSubmission(content));
    const nextLocalReviews = {};
    const nextReviewNotes = {};

    group.contents.forEach((content) => {
      const versionIndex = getCurrentVersionIndex(content);
      if (versionIndex < 0) {
        return;
      }

      const dashboardKey = getDashboardReviewKey(content, versionIndex);
      const localReview = dashboardReviews[dashboardKey];
      if (!localReview || typeof localReview.approved != "boolean") {
        return;
      }

      const panelKey = `${content.contentID || content.id}_${versionIndex}`;
      nextLocalReviews[panelKey] = {
        approved: localReview.approved,
        notes: localReview.reviewNotes || "",
      };
      nextReviewNotes[panelKey] = localReview.reviewNotes || "";
    });

    setReviewPanelGroup({ ...group, bidID: bidId, bidId });
    setReviewPanelSubmissions(submissions);
    setReviewPanelLocalReviews(nextLocalReviews);
    setReviewPanelReviewNotes(nextReviewNotes);
    setShowReviewPanel(true);
  }

  function closeReviewPanel() {
    setShowReviewPanel(false);
    resetReviewPanelState();
  }

  const handleReviewToggle = (key, approved) => {
    const bidId = reviewPanelGroup?.bidID || reviewPanelGroup?.bidId || "unknown";
    const separatorIndex = key.lastIndexOf("_");
    const contributionId = separatorIndex >= 0 ? key.slice(0, separatorIndex) : key;
    const versionIndex = separatorIndex >= 0 ? key.slice(separatorIndex + 1) : "0";
    const dashboardKey = `${bidId}_${contributionId}_${versionIndex}`;

    setReviewPanelLocalReviews((currentReviews) => ({
      ...currentReviews,
      [key]: {
        approved,
        notes: reviewPanelReviewNotes[key] || "",
      },
    }));

    setDashboardReviews((currentReviews) => ({
      ...currentReviews,
      [dashboardKey]: {
        approved,
        reviewNotes: reviewPanelReviewNotes[key] || currentReviews[dashboardKey]?.reviewNotes || "",
      },
    }));
  };

  const handleReviewNoteChange = (key, note) => {
    const bidId = reviewPanelGroup?.bidID || reviewPanelGroup?.bidId || "unknown";
    const separatorIndex = key.lastIndexOf("_");
    const contributionId = separatorIndex >= 0 ? key.slice(0, separatorIndex) : key;
    const versionIndex = separatorIndex >= 0 ? key.slice(separatorIndex + 1) : "0";
    const dashboardKey = `${bidId}_${contributionId}_${versionIndex}`;

    setReviewPanelReviewNotes((currentNotes) => ({
      ...currentNotes,
      [key]: note,
    }));

    setDashboardReviews((currentReviews) => ({
      ...currentReviews,
      [dashboardKey]: {
        approved:
          typeof currentReviews[dashboardKey]?.approved == "boolean"
            ? currentReviews[dashboardKey].approved
            : typeof reviewPanelLocalReviews[key]?.approved == "boolean"
              ? reviewPanelLocalReviews[key].approved
              : null,
        reviewNotes: note,
      },
    }));
  };

  const handleSelectedVersionChange = (submissionId, versionIdx) => {
    setReviewPanelSelectedVersion((currentSelected) => ({
      ...currentSelected,
      [submissionId]: versionIdx,
    }));
  };

  const handleExpandToggle = (submissionId) => {
    setReviewPanelExpanded((currentExpanded) => ({
      ...currentExpanded,
      [submissionId]: !currentExpanded[submissionId],
    }));
  };

  const handleContributionReview = async (reviewPayloads, closeAfter = false) => {
    if (!closeAfter) {
      return;
    }

    const allApproved = Array.isArray(reviewPayloads) && reviewPayloads.length > 0 && reviewPayloads.every((review) => review.approved);
    if (reviewPayloads && reviewPayloads.length > 0) {
      setAlert({
        display: "toast",
        visible: true,
        title: allApproved ? "All Approved" : "Contributions Reviewed",
        content: allApproved ? "All contributions have been approved." : "Some contributions were rejected.",
        className: allApproved ? "bg-success text-white" : "bg-danger text-white",
      });
    }

    closeReviewPanel();

    const currentBidID = reviewPanelGroup?.bidID || reviewPanelGroup?.bidId;
    if (currentBidID) {
      setDashboardReviews((currentReviews) => {
        const nextReviews = { ...currentReviews };
        Object.keys(nextReviews).forEach((key) => {
          if (key.startsWith(`${currentBidID}_`)) {
            delete nextReviews[key];
          }
        });
        return nextReviews;
      });
    }

    setCampaignData([]);
    setCampaignDataIDs([]);
    setScrollerRefreshToken((current) => current + 1);
    await getCampaign(campaignID);
  };

  useEffect(() => {
    if (campaign == null) {
      getCampaign(campaignID);
    }
  }, [campaign, campaignID]);

  useEffect(() => {
    let tempContributors = structuredClone(contributors);
    tempContributors = tempContributors.filter((contributor) => {
      const reviewSummary = reviewSummaryByInfluencer[contributor.id] || { pending: 0 };
      return (
        contributor.name.toLowerCase().includes(searchFilters.search) &&
        (searchFilters.posts ? contributor.contributions > 0 : true) &&
        (searchFilters.notReviewed ? reviewSummary.pending > 0 : true)
      );
    });

    if (!searchFilters.showAll) {
      tempContributors = tempContributors.slice(0, CONTRIBUTOR_PREVIEW_COUNT);
    }
    setFilteredContributors(tempContributors);
  }, [contributors, searchFilters, reviewSummaryByInfluencer]);

  if (error) {
    return (
      <div className="text-danger text-center h-100 d-flex align-items-center justify-content-start px-4 px-md-0 py-5 flex-column fs-5">
        <img src={NoAccess} className="noAccessImg mt-5" alt="No access" />
        <div>Error: {error}</div>
      </div>
    );
  }

  if (isLoaded) {
    return (
      <div className="bg-page campaignCTADashboard">
        <Topbar />
        <div className="pageName">Campaign UGC Dashboard</div>
        <AlertModal modal={showAlert} onClose={() => setAlert({ display: "toast", visible: false, title: "", content: "" })} />
        {!currentProfile.profile?.features?.includes("campaignDashboard") && !currentProfile.profile?.features?.includes("all") ? (
          <div className="bg-light text-center py-2 CampaignDashboardWarning">
            <div>
              <h3>Campaign Dashboard Not Enabled</h3>
              <p>
                Please speak to your account manager or contact
                <a href="mailto:customer.success@theinfluenceroom.com"> customer.success@theinfluenceroom.com</a> to discuss.
              </p>
            </div>
          </div>
        ) : (
          <>
            <Container className="fullscreenWithTopBar px-0" fluid>
              <Container className="pageTitleContainer px-0 shadow-sm pb-0" fluid>
                <Container className="border-bottom pb-0" fluid>
                  <Container className="px-0">
                    <Row className="px-3">
                      <Col xs={6} className="pt-3 mb-3">
                        <Link to="/Campaigns" className="text-light">
                          <svg width="15" height="13" className="align-middle" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M0.157595 5.79154L3.5281 9.88415C3.80704 10.1963 4.25408 10.2261 4.54761 9.98575C4.84116 9.74544 4.88952 9.25978 4.64905 8.96624L2.249 6.05465L12.2777 6.05465C12.6766 6.05465 13 5.73122 13 5.33237C13 4.93353 12.6766 4.6101 12.2777 4.6101L2.249 4.6101L4.64905 1.69851C4.88935 1.40513 4.83528 0.92619 4.54762 0.678997C4.25088 0.424079 3.76842 0.487055 3.52811 0.780596L0.157595 4.87321C-0.0618908 5.1817 -0.0430826 5.4927 0.157595 5.79113L0.157595 5.79154Z"
                              fill="var(--lightColor)"
                            />
                          </svg>{" "}
                          My Campaigns
                        </Link>
                        <div>
                          <h1 className="pageTitle tragedyRegularFont text-truncate mb-0 mt-2">{campaign ? campaign.name : "?"}</h1>
                        </div>
                      </Col>
                      <Col xs={6} className="mb-2 mb-lg-4 d-flex justify-content-end align-items-center">
                        <Button variant="secondary" size="sm" className="px-2 py-1 d-flex align-items-center me-2 mt-4" onClick={() => downloadContent()}>
                          {requestingDownload ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Requesting...
                            </>
                          ) : (
                            "Download All Content"
                          )}
                        </Button>
                        <div className="mt-2 mt-lg-4 fw-normal bg-light py-2 px-3 rounded">
                          <span className="fw-500 fs-6 text-primary">Total Posts: </span>
                          <span className="fs-6 text-primary">{campaign ? campaign.stats.contentCount : "?"}</span>
                        </div>
                      </Col>
                    </Row>
                  </Container>
                </Container>
              </Container>
              <Container className="campaignPhotoWall" fluid>
                <Container className="px-0 mb-3 mt-3 pt-md-2">
                  <Row className="px-md-4 mb-4">
                    <div className="col-12 pe-2">
                      <Row className="pb-4 px-2 ms-0 border border-secondary bg-light rounded shadow-sm bg-secondary position-relative">
                        <div className="col-12 pt-4 mb-2">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex w-75">
                              <input
                                type="text"
                                className="form-control-sm border me-2 w-100"
                                value={searchFilters.search}
                                placeholder="Search..."
                                onKeyDown={(e) => e.keyCode == 13 && updateSearchFilters("update", true)}
                                onChange={(e) => updateSearchFilters("search", e.target.value.toLowerCase())}
                              />
                              <button type="button" className="btn btn-sm btn-primary" onClick={() => updateSearchFilters("update", true)}>
                                Search
                              </button>
                            </div>
                            <button type="button" className="btn btn-sm btn-primary" onClick={hideShowFilters}>
                              Filters
                            </button>
                          </div>
                          <div id="filtersToggle" className={showFilters ? "d-block" : ""}>
                            <Form>
                              <Form.Check type="switch" checked={searchFilters.posts} id="has-posts" label="Has Posts" onChange={(e) => updateSearchFilters("posts", e.target.checked)} />
                              <Form.Check
                                type="switch"
                                checked={searchFilters.notReviewed}
                                id="not-reviewed"
                                label="Not Reviewed"
                                onChange={(e) => updateSearchFilters("notReviewed", e.target.checked)}
                              />
                            </Form>
                          </div>
                        </div>
                        <Row id="influencerList" className={searchFilters.showAll ? "influencerListModal" : "influencerList"}>
                          {filteredContributors.length == 0 && <p>No influencers match this search</p>}
                          {filteredContributors.map((contributor) => (
                            <div
                              className={`col-3 d-flex align-items-center influencerFilterSelection ${
                                selectedInfluencer == null || selectedInfluencer == contributor.id ? "" : "unselectedInfluencer"
                              } pt-3`}
                              key={contributor.id}
                            >
                              <img
                                src={getImageUrl(providers, contributor.profileImage, ["w_80", "h_80", "c_scale"])}
                                height="40px"
                                className="rounded border me-2"
                                onClick={() => toggleSelectedInfluencer(contributor.id)}
                                alt={contributor.name}
                              />
                              <div className="position-relative" onClick={() => toggleSelectedInfluencer(contributor.id)}>
                                <div className="contributorName">
                                  <b>{contributor.name}</b>
                                </div>
                                <div className="postCount smaller">
                                  {contributor.contributions} {contributor.contributions == 1 ? "Post" : "Posts"}
                                </div>
                              </div>
                            </div>
                          ))}
                        </Row>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary fw-bold viewMoreBtn"
                          onClick={() => updateSearchFilters("showAll", !searchFilters.showAll)}
                        >
                          {searchFilters.showAll ? "View Less" : "View More"}
                        </button>
                      </Row>
                    </div>
                  </Row>
                </Container>

                <Container className="px-0 pt-md-2">
                  {openCarousel && openCarousel.images && openCarousel.images.length > 0 && (
                    <Fade in={true}>
                      <div id="imageViewer">
                        <div className="carouselControl">
                          <button type="button" onClick={() => setOpenCarousel(null)} className="btn btn-sm btn-light closeImageViewer">
                            Close
                          </button>
                        </div>
                        <Carousel activeIndex={openCarousel.openImage} onSelect={(e) => changeContentImage(e)} slide={false}>
                          <Carousel.Item>
                            <img id="0" className="d-block" src={getCarouselImageUrl(openCarousel.images[0])} alt={openCarousel.images[0]?.caption || "Content"} />
                          </Carousel.Item>
                          {openCarousel.images.slice(1).map((contentImage, index) => (
                            <Carousel.Item key={index}>
                              <img id={index + 1} src={getCarouselImageUrl(contentImage)} className="d-block" alt={contentImage.caption || "Content"} />
                            </Carousel.Item>
                          ))}
                        </Carousel>
                      </div>
                    </Fade>
                  )}

                  <Row className="px-md-3">
                    <div className="col-12 mb-5">
                      {groupedCampaignData.length == 0 && (
                        <div className="bg-light rounded p-4 text-center text-muted">No self-submitted content found for this campaign.</div>
                      )}

                      {groupedCampaignData.map((group) => {
                        const summary = reviewSummaryByInfluencer[group.id] || {
                          approved: 0,
                          rejected: 0,
                          pending: 0,
                          total: group.contents.length,
                        };
                        const bidId = getGroupBidId(group);
                        const hasReviewableContent = group.contents.some((content) => isContentReviewable(content));

                        return (
                          <div className="mb-5" key={group.id}>
                            <div className="bg-light rounded p-3 d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3 border">
                              <div className="d-flex align-items-center mb-2 mb-md-0">
                                <img
                                  src={getImageUrl(providers, group.profileImage, ["w_80", "h_80", "c_scale"])}
                                  height="42px"
                                  className="rounded border me-2"
                                  alt={group.name}
                                />
                                <div>
                                  <div className="fw-bold">{group.name}</div>
                                  <div className="small text-muted">
                                    {summary.total} {summary.total == 1 ? "Post" : "Posts"}
                                  </div>
                                  <div className="small mt-1">
                                    <span className={`badge ${getBidStatePillClass(group.state)}`}>{group.state || "Unknown"}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex flex-column flex-md-row align-items-md-center">
                                <div className="small text-muted me-md-3 mb-2 mb-md-0">
                                  {summary.approved} approved | {summary.rejected} rejected | {summary.pending} pending
                                </div>
                                {hasReviewableContent && (
                                  <>
                                    <div className="btn-group btn-group-sm me-2 mb-2 mb-md-0">
                                      <button
                                        type="button"
                                        className="btn btn-dark"
                                        onClick={() => openGroupReviewPanel(group)}
                                        disabled={!bidId}
                                        title="Open full per-bid review panel"
                                      >
                                        Review
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            <Masonry breakpointCols={breakpointColumnsObj} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
                              {group.contents.map((content) => {
                                const reviewState = getReviewState(content, dashboardReviews);
                                const reviewable = isContentReviewable(content);
                                const reviewKey = getDashboardReviewKey(content);
                                let videoSource = getVideoSource(content);
                                let imageSource = getImageSource(content);
                                const imageSlides = getImageSlides(content);
                                let approvalClass = "bg-warning text-dark";
                                let approvalText = "Pending Review";
                                if (reviewState.reviewed && reviewState.approved === true) {
                                  approvalClass = "bg-success";
                                  approvalText = reviewState.isDraft ? "Approved (Draft)" : "Approved";
                                } else if (reviewState.reviewed && reviewState.approved === false) {
                                  approvalClass = "bg-danger";
                                  approvalText = reviewState.isDraft ? "Rejected (Draft)" : "Rejected";
                                }

                                return (
                                  <div key={content.id} className="position-relative mb-4 ratingHover">
                                    <div className="bg-light p-2 rounded-top d-flex align-items-center justify-content-between">
                                      <span className={`badge ${approvalClass}`}>{approvalText}</span>
                                    </div>

                                    {(content.assetType == "story" || content.assetType == "video") && videoSource && (
                                      <video src={videoSource} className="w-100" controls preload="metadata" />
                                    )}
                                    {(content.assetType == "story" || content.assetType == "video") && !videoSource && (
                                      <div className="p-3 bg-light border text-muted small">Video unavailable</div>
                                    )}
                                    {content.assetType == "image" && imageSource && imageSlides.length > 0 && (
                                      <img
                                        src={imageSource}
                                        className="w-100 cursor-pointer"
                                        alt="Campaign content"
                                        onClick={() => setOpenCarousel({ images: imageSlides, openImage: imageSlides.length - 1 })}
                                      />
                                    )}
                                    {content.assetType == "image" && imageSource && imageSlides.length == 0 && (
                                      <img src={imageSource} className="w-100" alt="Campaign content" />
                                    )}
                                    {content.assetType == "image" && !imageSource && (
                                      <div className="p-3 bg-light border text-muted small">Image unavailable</div>
                                    )}
                                    {content.assetType == "link" && (
                                      <a
                                        href={typeof content.assetUrl == "string" ? content.assetUrl : content.assetUrl?.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="d-block p-4 text-center text-decoration-none text-dark border"
                                      >
                                        {typeof content.assetUrl == "string" ? content.assetUrl : content.assetUrl?.url}
                                      </a>
                                    )}

                                    <div className="bg-light p-2">
                                      {reviewable ? (
                                        <>
                                          <Form.Control
                                            size="sm"
                                            type="text"
                                            placeholder="Review notes (optional)"
                                            value={dashboardReviews[reviewKey]?.reviewNotes || reviewState.reviewNotes || ""}
                                            onChange={(event) => setDashboardContentReviewNote(content, event.target.value)}
                                            className="mb-2"
                                          />
                                          <div className="d-flex justify-content-end">
                                            <button
                                              type="button"
                                              className={`btn btn-sm me-2 ${
                                                reviewState.reviewed && reviewState.approved === true ? "btn-success" : "btn-outline-success"
                                              }`}
                                              onClick={() => setDashboardContentReview(content, true)}
                                            >
                                              Approve
                                            </button>
                                            <button
                                              type="button"
                                              className={`btn btn-sm ${
                                                reviewState.reviewed && reviewState.approved === false ? "btn-danger" : "btn-outline-danger"
                                              }`}
                                              onClick={() => setDashboardContentReview(content, false)}
                                            >
                                              Reject
                                            </button>
                                          </div>
                                          {reviewState.isDraft && <div className="small text-muted mt-2">Saved locally. Submit from Full Review when ready.</div>}
                                        </>
                                      ) : (
                                        <>
                                          <div className="small text-muted">Reviewed</div>
                                          {reviewState.reviewNotes ? (
                                            <div className="small fst-italic mt-1">{reviewState.reviewNotes}</div>
                                          ) : (
                                            <div className="small text-muted mt-1">No review notes.</div>
                                          )}
                                        </>
                                      )}
                                    </div>

                                    <div className="bg-light p-2 small rounded-bottom text-truncate">{content.text}</div>
                                  </div>
                                );
                              })}
                            </Masonry>
                          </div>
                        );
                      })}
                    </div>
                  </Row>

                  <PageScroller
                    key={`ugc-scroller-${selectedInfluencer || "all"}-${scrollerRefreshToken}`}
                    filter={`${selectedInfluencer || "all"}-${scrollerRefreshToken}`}
                    endpoint={`campaign/ugcContent?platform=self`}
                    setState={getMoreContent}
                    pageSize={10}
                  />
                </Container>
              </Container>
            </Container>

            {showReviewPanel && reviewPanelGroup && (
              <ReviewPanel
                show={showReviewPanel}
                onHide={closeReviewPanel}
                collaborator={{
                  profile: { name: reviewPanelGroup.name },
                  campaignType: campaign?.campaignType,
                  bidState: reviewPanelGroup.state,
                  details: { id: reviewPanelGroup.bidID || reviewPanelGroup.bidId },
                }}
                submissions={reviewPanelSubmissions}
                campaignId={campaignID}
                bidId={reviewPanelGroup.bidID || reviewPanelGroup.bidId}
                onReview={handleContributionReview}
                providers={providers}
                localReviews={reviewPanelLocalReviews}
                onReviewToggle={handleReviewToggle}
                reviewNotes={reviewPanelReviewNotes}
                onReviewNoteChange={handleReviewNoteChange}
                selectedVersion={reviewPanelSelectedVersion}
                onSelectedVersionChange={handleSelectedVersionChange}
                expanded={reviewPanelExpanded}
                onExpandToggle={handleExpandToggle}
                contentReviewEnabled={campaign?.contentReviewEnabled !== false}
              />
            )}
          </>
        )}
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-page campaignCTADashboard">
      <Topbar />
      <Container className="fullscreenWithTopBar px-0" fluid>
        <FullPageSpinner />
      </Container>
    </div>
  );
}
